import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { AppDispatch, RootState } from '../../Redux/Store';
import {
  cancelManagedReservation,
  fetchManagedReservation,
} from '../../Redux/PublicReservation/publicReservationApi';
import { resetPublicReservation } from '../../Redux/PublicReservation/publicReservationSlice';
import {
  PaymentStatus,
  RESERVATION_STATUS_COLOR,
} from '../../types/reservation';
import {
  makeT,
  publicStatusKey,
  PublicReservationDictKey,
  PublicReservationLang,
} from '../../i18n/publicReservationI18n';
import PublicHeader from '../../Components/Header/PublicHeader';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import {
  LanguageToggle,
  PublicReservationFooter,
  formatPublicDateTime,
  googleMapsUrl,
  parseApiErrorMessage,
} from './shared';

/**
 * 公開の予約照会・セルフキャンセルページ（認証不要・/reservations?no=&token=&store=）。
 * 予約完了メール内のリンクから遷移し、予約番号 + 管理トークンで本人照会する。
 * 表示文言はすべて publicReservationI18n の辞書経由で ja/en を切り替える（ADR 0040）。
 */

/** 支払いステータス → 表示ラベルのキー */
const paymentStatusKey = (status: PaymentStatus): PublicReservationDictKey => {
  switch (status) {
    case 'AUTHORIZED':
      return 'paymentAuthorized';
    case 'CAPTURED':
      return 'paymentCaptured';
    case 'CANCELED':
      return 'paymentCanceled';
    case 'PARTIALLY_REFUNDED':
      return 'paymentPartiallyRefunded';
    case 'REFUNDED':
      return 'paymentRefunded';
    case 'FAILED':
    default:
      return 'paymentFailed';
  }
};

/** 詳細カードの 1 行 */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
      py: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right' }}>
      {typeof value === 'string' ? (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

const ReservationLookup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const reservationNumber = searchParams.get('no');
  const token = searchParams.get('token');
  const storeId = searchParams.get('store');
  const paramsOk = !!reservationNumber && !!token && !!storeId;

  const dispatch = useDispatch<AppDispatch>();
  const { managed, managedLoading, managedError, cancelling, cancelError, cancelled } =
    useSelector((state: RootState) => state.publicReservation);

  const [lang, setLang] = useState<PublicReservationLang>('ja');
  const t = useMemo(() => makeT(lang), [lang]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (storeId && reservationNumber && token) {
      dispatch(fetchManagedReservation({ storeId, reservationNumber, token }));
    }
    return () => {
      dispatch(resetPublicReservation());
    };
  }, [dispatch, storeId, reservationNumber, token]);

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    if (storeId && reservationNumber && token) {
      dispatch(cancelManagedReservation({ storeId, reservationNumber, token }));
    }
  };

  const activeStatus = managed?.status === 'REQUESTED' || managed?.status === 'CONFIRMED';
  // cancellable 未定義は旧 BE レスポンス互換として REQUESTED / CONFIRMED ならボタンを出す
  const showCancelButton =
    !!managed &&
    !cancelled &&
    (managed.cancellable === true || (managed.cancellable === undefined && activeStatus));
  const deadlinePassed =
    !!managed && !cancelled && managed.cancellable === false && activeStatus;

  return (
    <Box>
      <PublicHeader />
      <Container maxWidth="sm" sx={{ mt: 3, mb: 6 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('lookupTitle')}
          </Typography>
          <LanguageToggle lang={lang} onChange={setLang} />
        </Box>

        {!paramsOk ? (
          <Alert severity="error">{t('lookupNotFound')}</Alert>
        ) : managedLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Box>
        ) : managedError ? (
          <Alert severity="error">{t('lookupNotFound')}</Alert>
        ) : managed ? (
          <>
            {cancelled && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('cancelled')}
              </Alert>
            )}
            {cancelError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {parseApiErrorMessage(cancelError, t('cancelError'))}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <DetailRow label={t('reservationNumber')} value={managed.reservationNumber} />
              <DetailRow
                label={t('status')}
                value={
                  <Chip
                    size="small"
                    label={t(publicStatusKey(managed.status))}
                    color={RESERVATION_STATUS_COLOR[managed.status]}
                  />
                }
              />
              <DetailRow
                label={t('dateTime')}
                value={
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {formatPublicDateTime(lang, managed.reservationTime)}
                    </Typography>
                    {/* 確定した来店予定時刻である旨の注記（B7） */}
                    <Typography variant="caption" color="text.secondary">
                      {t('visitDateTimeNote')}
                    </Typography>
                  </Box>
                }
              />
              <DetailRow
                label={t('partySize')}
                value={`${managed.numberOfPeople}${t('people')}`}
              />
              <DetailRow label={t('guestName')} value={managed.userName} />
              {managed.userEmail && <DetailRow label={t('email')} value={managed.userEmail} />}
              {managed.userPhone && <DetailRow label={t('phone')} value={managed.userPhone} />}
              {managed.selectedMenuName && (
                <DetailRow
                  label={t('course')}
                  value={(() => {
                    // 税込金額を優先表示（フィードバックC）。BE が税込を返さない旧レスポンスは税抜のまま表示する。
                    const taxIncluded =
                      managed.selectedMenuPriceIncludingTax != null
                        ? managed.selectedMenuPriceIncludingTax
                        : null;
                    if (taxIncluded != null) {
                      return `${managed.selectedMenuName} ¥${taxIncluded.toLocaleString()}${t('taxIncluded')}`;
                    }
                    return managed.selectedMenuPrice !== null
                      ? `${managed.selectedMenuName} ¥${managed.selectedMenuPrice.toLocaleString()}`
                      : managed.selectedMenuName;
                  })()}
                />
              )}
              {managed.responses.map((response) => (
                <DetailRow
                  key={response.question}
                  label={response.question}
                  value={response.answer}
                />
              ))}
              {managed.payment && (
                <DetailRow
                  label={t('paymentAmount')}
                  value={`¥${managed.payment.amount.toLocaleString()} (${t(
                    paymentStatusKey(managed.payment.status),
                  )})`}
                />
              )}
              {managed.payment && managed.payment.refundedAmount > 0 && (
                <DetailRow
                  label={t('refundedAmount')}
                  value={`¥${managed.payment.refundedAmount.toLocaleString()}`}
                />
              )}
            </Paper>

            {/* 店舗名・住所・連絡先の案内（B7）。住所は Google マップリンク。設定済み項目のみ表示。 */}
            {(managed.storeName?.trim() ||
              managed.address?.trim() ||
              managed.contactInfo?.trim()) && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('storeGuideTitle')}
                </Typography>
                {managed.storeName?.trim() && (
                  <DetailRow label={t('storeNameLabel')} value={managed.storeName.trim()} />
                )}
                {managed.address?.trim() && (
                  <DetailRow
                    label={t('addressLabel')}
                    value={
                      <Link
                        href={googleMapsUrl(managed.address.trim())}
                        target="_blank"
                        rel="noopener"
                      >
                        {managed.address.trim()}
                      </Link>
                    }
                  />
                )}
                {managed.contactInfo?.trim() && (
                  <DetailRow label={t('storeContactLabel')} value={managed.contactInfo.trim()} />
                )}
              </Paper>
            )}

            {deadlinePassed && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('cancelDeadlinePassed')}
              </Alert>
            )}
            {showCancelButton && (
              <Button
                color="error"
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                disabled={cancelling}
                onClick={() => setConfirmOpen(true)}
              >
                {cancelling ? <CircularProgress size={22} /> : t('cancelButton')}
              </Button>
            )}
          </>
        ) : null}

        {/* サービス提供者（FEAST株式会社）の明示とユーザー情報の取り扱いリンク（フィードバック3） */}
        <PublicReservationFooter lang={lang} />
      </Container>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleCancelConfirm}
        title={t('cancelConfirmTitle')}
        message={t('cancelConfirmBody')}
        confirmText={t('cancelYes')}
        cancelText={t('cancelNo')}
        confirmColor="error"
        closeColor="inherit"
      />
    </Box>
  );
};

export default ReservationLookup;
